import { Injectable } from '@angular/core';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { lastValueFrom } from 'rxjs';
import { OlPdfAcceptanceComponent } from '../components/ol-pdf-acceptance/ol-pdf-acceptance.component';
import { CO_STATUS_CONST } from '../utils/utils';
import { AuthService } from './auth.service';
import { PropostaSalarialService } from './proposta-salarial.service';

@Injectable({
  providedIn: 'root',
})
export class PdfService {
  private dominioId: string;

  constructor(
    private authService: AuthService,
    private storage: AngularFireStorage,
    private propostaService: PropostaSalarialService
  ) {
    this.dominioId = this.authService.dominioId;
  }

  async getPdfConfig(olPdfAcceptance: OlPdfAcceptanceComponent) {
    const html2pdf = (await import('html2pdf.js')).default;
    const element = olPdfAcceptance.pdfArea.nativeElement;
    const opt: any = {
      margin: [0, 0, 0, 0],
      filename: `Offer - ${olPdfAcceptance.propostaSalarial.nomeCompleto} .pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 1.5, useCORS: false, logging: false },
      jsPDF: { unit: 'mm', format: 'a1', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'], before: '.page-break' },
    };
    return { html2pdf, element, opt };
  }

  async downloadPdf(olPdfAcceptance: OlPdfAcceptanceComponent) {
    const { html2pdf, element, opt } = await this.getPdfConfig(olPdfAcceptance);
    await html2pdf().set(opt).from(element).save();
  }

  private waitForTemplate(olPdfAcceptance: OlPdfAcceptanceComponent, timeoutMs = 5000): Promise<void> {
    return new Promise((resolve) => {
      if (olPdfAcceptance.currentTemplate) { resolve(); return; }
      const interval = setInterval(() => {
        if (olPdfAcceptance.currentTemplate) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
      setTimeout(() => { clearInterval(interval); resolve(); }, timeoutMs);
    });
  }

  async convertToBlob(
    olPdfAcceptance: OlPdfAcceptanceComponent
  ): Promise<Blob> {
    await this.waitForTemplate(olPdfAcceptance);
    // Yield to Angular's change detection so [innerHTML] bindings render before html2pdf captures the DOM.
    await new Promise(resolve => setTimeout(resolve, 0));
    const { html2pdf, element, opt } = await this.getPdfConfig(olPdfAcceptance);
    const pdf = await html2pdf().set(opt).from(element).toPdf().get('pdf');
    const blob: Blob = pdf.output('blob');

    return blob;
  }

  async processAndUploadPdf(
    olPdfAcceptance: OlPdfAcceptanceComponent,
    desiredStatus?: number
  ): Promise<{ path: string; downloadURL: string } | null> {
    const MAX_RETRIES = 3;
    let lastError: unknown;

    const domainId = this.authService.dominioId;
    const candidateId =
      this.propostaService.getView()?.id || this.authService.userId;
    const currentStatus = Number(
      this.propostaService.getStatus$().value ??
      this.propostaService.getView()?.status
    );
    const targetStatus =
      typeof desiredStatus === 'number' ? desiredStatus : currentStatus;
    const folder =
      targetStatus === CO_STATUS_CONST.RECUSADO
        ? 'rejected_proposals'
        : 'accepted_proposals';
    const path = `${domainId}/${folder}/${candidateId}.pdf`;
    const metadata = { contentType: 'application/pdf' } as const;

    // Blob must be generated before navigation destroys the DOM element.
    // Generate once and reuse across retries.
    const blob = await this.convertToBlob(olPdfAcceptance);

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const uploadTask = this.storage.upload(path, blob, metadata);
        await lastValueFrom(uploadTask.snapshotChanges());

        const fileRef = this.storage.ref(path);
        const downloadURL = await lastValueFrom(fileRef.getDownloadURL());

        await this.propostaService.saveProposalPdf(path);
        return { path, downloadURL };
      } catch (error) {
        lastError = error;
        if (attempt < MAX_RETRIES) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }
    }

    throw lastError;
  }
}
